import { Button } from '@repo/ui/components/button';
import { IconLogout, IconMenu2, IconX } from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { NavLink } from 'react-router';

import lockupUrl from '@/assets/brand/garden-lockup-reversed.png';
import { useSession } from '@/auth/session';

import { OpenDaysWarning } from './day-close/OpenDaysWarning';
import { navigationFor, navigationSections } from './navigation';
import { RefusedWritesLink } from './queued-writes/RefusedWritesLink';
import { BackToTill } from './till/BackToTill';
import { labelForRole } from './users/user-roles';

/**
 * The frame every signed-in back-office screen renders inside: who you are, what
 * you can reach, and the way out.
 *
 * `paths.ts` records that this ticket owns the signed-in shell, so it is here
 * rather than in the first screen that needed one — which is how eighteen
 * screens end up with eighteen headers. The till is a different shell and is not
 * this one (`docs/ui-conventions.md`); a till screen has the current sale and a
 * way back, and no margin.
 *
 * ## The panel and the rail
 *
 * Settled 26 Aug 2026, and **this replaces "The margin is the plate"** — 25 Aug,
 * superseded here. `docs/design-language.md` records the same supersession under
 * **The panel and the rail**; read it there for the language and here for what
 * the markup does about it.
 *
 * The margin's rotation stands. What goes is its *tone*: enamel chrome beside an
 * enamel field, one step apart from the mist behind both. Three surfaces were
 * being asked of two values, and the boundary that was supposed to carry the
 * whole structure was 1.17:1. It read as unfinished because it was one level
 * pretending to be three.
 *
 * - **The ground is black and the rail shares it.** Revised 27 Aug 2026: the
 *   panel is no longer a surface. It is the row that holds the rail and the
 *   sheet, inset `14px` at `h-[calc(100vh-28px)]`, with the page showing
 *   through it. The rail merges into that ground, and that is the point — it
 *   leaves the white sheet as the whole subject of the screen.
 * - **The sheet carries the enamel and its own four corners.** Until 27 Aug the
 *   radius and `overflow-hidden` were on the panel, so the field was rounded on
 *   the right and butted flat against the rail on the left. On the field, its
 *   left corners notch the ground and it reads as a sheet laid on the page.
 *   Every edge of the app is drawn either way, which is what **Structure**'s
 *   *"nothing floats in a void"* asks for. Below `lg` the inset and the radius
 *   go: a 390px screen wants the whole viewport.
 * - **`--ground` is a new value and `--background` did not move.** Fourteen
 *   `bg-background` and one `text-background` mean mist and are correct —
 *   the toolbar's recessed controls, and Staffing's checkbox tick, which would
 *   have gone black-on-slate and vanished. `--foreground` stays slate too:
 *   *"wet slate, not black"* is an argument about ink on white and still holds.
 * - **The reversed lockup, directly.** `AuthFrame.Mark` swaps the two cuts on
 *   `dark:` and that will not fire here — this rail is dark in a light document.
 *   There is nothing to swap: the surface under it is slate in every theme this
 *   app can reach, so the cut is stated rather than chosen.
 * - **The active pill keeps its argument and loses its colour.** It was
 *   `bg-muted` on enamel and it is `bg-sidebar-primary` on slate — a white tint,
 *   still neutral, still shape-not-colour. `RefusedWritesLink` is still the only
 *   tinted thing in the rail, which is what keeps a colour here meaning
 *   something has gone wrong.
 * - **The panel's stated height is what the field's flex chain was missing.**
 *   `Screen.tsx`'s own comment records `lg:h-screen lg:flex-none` as the fix for
 *   a `flex-1` resolving against a `min-h-screen` parent whose height was its
 *   content's. This box is `h-[calc(100vh-28px)]`, so the number is definite at
 *   the top of the chain and `main` can go back to `flex-1`.
 *
 * The navigation is filtered by role, and that filtering is a courtesy. See
 * `navigation.ts`: the role comes from a value the user can edit, the guard
 * refuses the route regardless of what is drawn here, and the server refuses it
 * after that.
 */
export function BackOfficeShell({ children }: { children: ReactNode }) {
  const { session, signOut } = useSession();

  // Below `lg` the index is behind a disclosure, and this is whether it is
  // open. It is meaningless at desk width, where the margin's classes show
  // the index unconditionally — which is why nothing here watches the viewport.
  // A width read in JS is a second answer to a question CSS is already
  // answering, and the two disagree on the first paint.
  const [menuOpen, setMenuOpen] = useState(false);

  const viewer =
    session === null
      ? null
      : {
          role: session.user.role,
          mustChangeCredential: session.user.mustChangeCredential,
        };

  const sections = navigationSections(navigationFor(viewer));

  return (
    // THE PANEL. One enamel surface on the mist ground, with all four edges
    // drawn and both of the app's regions inside it.
    //
    // `h-[calc(100vh-28px)]` is a *stated* height and that is the load-bearing
    // part, not the radius: everything below this — the rail's scrolling index,
    // the field's scrolling records — is a `flex-1` that needs a definite number
    // somewhere above it. `min-h-screen` gave it a height that was its own
    // content's, which is the failure `Screen.tsx` had to state `lg:h-screen` to
    // get around. It does not any more.
    //
    // Below `lg` there is no inset, no radius and no stated height: the margin
    // is a band across the top, the page scrolls the way a page on a phone does,
    // and a rounded panel would spend both edges of a 390px screen on nothing.
    <div className="flex min-h-screen flex-col lg:m-[14px] lg:h-[calc(100vh-28px)] lg:min-h-0 lg:flex-row">
      {/* THE RAIL. The plate, turned on its side and painted: slate ground,
          enamel lettering, gauge for everything idle.

          The old margin was enamel against an enamel field and the whole of the
          boundary was a 1.17:1 tonal step — which was accepted deliberately and
          is what this reverses. Three surfaces (ground, panel, rail) now do the
          work two values were being asked to do, and no rule or hairline is
          spent on the outer edge because the rail no longer needs one: it is a
          different colour from the field beside it.

          The datum is still not here. That reasoning survives untouched — the
          signature belongs to the signed-out frame, where it rules a card, and
          a slate line down the side of a screen somebody looks at all day
          states more than the chrome is saying. There is no slate line to draw
          any more; the rail *is* the slate.

          No `sticky` and no `h-screen`: the panel above states the height and
          this is a stretched item in its row, so the column is exactly as tall
          as the panel and the field beside it needs no left padding kept in
          step with a width declared somewhere else. */}
      <div className="flex shrink-0 flex-col bg-sidebar text-sidebar-foreground lg:w-64">
        {/* The lockup, not the name in type — and **the reversed cut, stated**.
            `AuthFrame.Mark` picks between the two on `dark:`, which cannot help
            here: this is a dark rail inside a light document, so the variant
            never fires. There is nothing to choose between. The surface under
            this mark is slate in every theme the app can reach.

            `h-6 w-auto`, so a re-cut asset changes its own width and moves
            nothing else. It does not scroll. The index below it does. */}
        <div className="flex items-center gap-3 px-5 pt-4 pb-5">
          <img src={lockupUrl} alt="Government Botanical Garden Munnar" className="h-6 w-auto" />

          {/* Below `lg` the margin would eat a 390px screen whole, so there it
              is a band across the top and the index is behind this. The
              alternative — keeping all twenty-two items in flow above every
              screen — is the failure this ticket exists to end, one column
              instead of three rows.

              A disclosure and not a drawer: a drawer needs a scrim, a focus
              trap and an escape key, which is three new behaviours in the
              chrome for a back office that is used at a desk. What is on a
              phone is somebody checking a figure.

              The registry's `ghost` hover is `bg-muted` with `text-foreground`,
              which on slate is a bright grey flash carrying dark ink. Both are
              restated against the rail's own family, and the focus ring with
              them: `--ring` is reed and cannot be seen here at all. */}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-expanded={menuOpen}
            aria-controls="back-office-index"
            aria-label={menuOpen ? 'Hide navigation' : 'Show navigation'}
            className="ml-auto text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring/30 lg:hidden"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <IconX size={18} stroke={1.7} /> : <IconMenu2 size={18} stroke={1.7} />}
          </Button>
        </div>

        <div
          id="back-office-index"
          className={['min-h-0 flex-1 flex-col', menuOpen ? 'flex' : 'hidden lg:flex'].join(' ')}
        >
          {/* The one scrolling part, and as of #211 it scrolls **on purpose**
              at every window size rather than only in a short one. The whole of
              that decision, with the numbers behind it, is in THE FOLD below.

              The mask is the device the till already uses for exactly this — see
              the basket in `till/SellGoods.tsx`, whose comment is the argument.
              A row cut in half against the foot rule reads as a rendering fault;
              the same cut under a fade reads as a list with more in it. It eats
              nothing but this element's own padding, which is why `pb-1.5` went
              to `pb-4`: the fade is 1rem and the padding is now the same 1rem,
              so scrolled to the end the mask eats the gap under the last item
              rather than the bottom half of its label. That is the pairing the
              till uses — `px-5 pb-4` against the same 1rem gradient — and the
              six pixels it costs were only ever worth arguing about while there
              was a fold to lose them to.

              `min-h-0` and `flex-1` are what keep the scroll inside the column
              rather than growing it: without them the height resolves against
              this element's own content, the index runs off the bottom of the
              panel, and its tail is drawn and unreachable. */}
          <nav
            aria-label="Back office"
            className="min-h-0 flex-1 overflow-y-auto [mask-image:linear-gradient(to_bottom,#000_calc(100%-1rem),transparent)] px-2.5 pt-1 pb-4"
            // Below `lg` the index is a disclosure over the page, so following a
            // link has to close it — otherwise the screen you asked for is
            // underneath the menu you asked it from. At `lg` the classes above
            // keep it open whatever this says.
            onClick={() => setMenuOpen(false)}
          >
            {sections.map((section) => (
              // Keyed on the first item's path rather than on the heading: an
              // ungrouped run has no heading to key on, and a path is unique in
              // the route table by construction.
              <div key={section.items[0]?.path ?? section.heading}>
                {/* Signage caps — the plate treatment already in the system, not
                    a new device. `docs/design-language.md` sanctions caps for
                    one- and two-word frame lettering, and these are exactly
                    that.

                    Gauge a step down rather than reed: reed is 2.19:1 on slate
                    and unreadable. `/75` is 6.14:1 at this size, which clears AA
                    with room; the prototype's `.6` measured 4.43:1 and missed it
                    at 10px, so this is a measured value and not the one that was
                    drawn.

                    THE FOLD IS GONE, AND THAT IS THE ANSWER RATHER THAN THE
                    PROBLEM (#211). What was here recorded the fold being broken
                    twice by spacing that looked fine, and ended by asking for
                    every addition to be re-measured at 1280x800. Nobody was
                    told to, nothing checked, and four correct pull requests
                    (#185, #186, #187, #188) each added one entry to a budget
                    with 7.5px left in it.

                    **Measured, not estimated, at exactly 1280x800 with all
                    twenty-two entries an owner sees.** The nav's content box
                    ran 60 → 722.5, which is 662.5px. The last item —
                    "Item export" — had its bottom at **835.0px**: 112.5px over,
                    scrolling quietly because this element was already
                    `overflow-y-auto`. Eighteen items and five headings come to
                    655px, which is the 7.5px-spare figure the old note above
                    shipped with, and it was accurate; the index simply outgrew
                    it. Afterwards, with the spacing below and the `pb-4` above:
                    875.0px against a content edge at 712.5 — 162.5px of
                    deliberate scroll, and sixteen rows above the fold.

                    **Re-measured after #216 gave the lockup its room**, same
                    window and same twenty-two entries. The mark's row ended at
                    54 and the first item began at 60: six pixels, which read as
                    another list item rather than as the head of the column. It
                    is 24 now — `pb-5` on the header block above and `pt-1` on
                    this element — and every number below moved by exactly that
                    eighteen. The content box runs 78 → 712.5, which is 634.5px;
                    "Item export" ends at **893.0px**, so the deliberate scroll
                    is **180.5px**.

                    **Sixteen rows still clear the fold, and that is measured
                    rather than assumed.** Eighteen pixels is most of a row and
                    it cost none, because the seventeenth entry is "Profit and
                    loss" — the first of REPORTS, so a 31px heading sits in
                    front of it and its bottom was already 725.0 against a
                    712.5 edge before this change. "Cash book" is still the last
                    row drawn whole. The next entry added to the four groups
                    above REPORTS is the one that will cost a row.

                    **The rail scrolls deliberately now.** The other two options
                    were measured before being dismissed, because that is the
                    mistake this note exists to stop:

                    - *Buy the rows back.* 112.5px across 22 rows is 5.11px a
                      row against a row padding of 5px top and 5px bottom — it
                      means deleting essentially all of it, 30px rows down to
                      25px, and it still fails at 26 entries and fails again the
                      moment this heading is given the room below.
                    - *Fold REPORTS behind a disclosure.* Six 30px rows back,
                      less a trigger row, is ~150px net — it fits today and buys
                      exactly four more entries, at the price of six shipped
                      screens behind a click and a third piece of state in the
                      rail.

                    Neither is true at twenty-six, and the park keeps opening
                    screens.

                    The fade is on the bottom edge only, and that is an argument
                    rather than an oversight: the cut that reads as a fault is
                    the one against the foot rule below, and the top of this
                    element butts onto the rail's own slate with nothing drawn
                    on it, where a row leaving the view is only a row leaving
                    the view.

                    So: **do not re-measure this against a fold, and do not
                    treat a twenty-third entry as a defect.** What has to stay
                    true is that the scroll reads as intentional — the mask on
                    the `nav` above, and the `min-h-0` chain that keeps the
                    index inside the column instead of running off the end of
                    it. `BackOfficeShell.test.tsx` holds both, and holds the
                    entry count, so that the next pull request to move it says
                    so in its own diff. That is the check the four above never
                    had.

                    **The heading's own spacing is the ask this ticket carried**
                    — `pt-3 pb-1`, 12px above and 4px below. It was `pt-1 pb-1`,
                    equally close to the item above it as to the ones it
                    introduces, so it read as attached to the wrong side. The
                    asymmetry is the point and not the size of it: a heading
                    belongs to the group beneath it. Five headings at 31px
                    instead of 23px is 40px more index, which is affordable
                    exactly because the fold is no longer the constraint. */}
                {section.heading !== null && (
                  <p className="px-3 pt-3 pb-1 font-display text-[0.625rem] font-semibold tracking-[0.16em] text-sidebar-accent-foreground/75 uppercase">
                    {section.heading}
                  </p>
                )}

                {section.items.map((item) => {
                  const Icon = item.icon;

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      // `aria-current="page"` is what react-router sets on the
                      // active link, so the current screen is announced rather
                      // than only marked.
                      //
                      // A filled pill in a white tint and not in the accent.
                      // The inversion changes the value and not the argument:
                      // the tint it once carried was `primary/10`, which put a
                      // second green thing on screen competing with the page's
                      // one primary action, and the design language allows
                      // exactly one accent per screen. The shape says "you are
                      // here" and the colour says nothing.
                      //
                      // The focus outline is stated because the base layer's
                      // `outline-ring/50` is reed, which the rail cannot show.
                      className={({ isActive }) =>
                        [
                          'flex items-center gap-2.5 rounded-full px-3 py-[0.3125rem] text-sm transition-colors',
                          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sidebar-ring',
                          isActive
                            ? 'bg-sidebar-primary font-medium text-sidebar-primary-foreground'
                            : 'text-sidebar-accent-foreground hover:text-sidebar-foreground',
                        ].join(' ')
                      }
                    >
                      {/* No `aria-hidden` needed and no title: the icon inherits
                          `currentColor`, so the active item's icon brightens
                          with its label, and the label beside it is the
                          accessible name. */}
                      <Icon size={18} stroke={1.7} />
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Who, and what they may do, at the foot of the rail. The role is
              the other half of "who and where", and it is the thing that
              explains why this person sees five navigation items and the next
              sees three. In signage caps because it is a one-word label on a
              plate; the name beside it is a name and stays in body.

              The rule is not the rail's edge — that is a colour now and needs
              no line at all — it is the line between a column that scrolls and
              a foot that does not. Without it a long index runs straight into
              the person's name. `border-sidebar-border` and not
              `border-border`: gauge is a hairline drawn on enamel and on slate
              it reads as a scratch. That white tint is the second of the two
              values this rail spends outside the six. */}
          <div className="border-t border-sidebar-border px-5 py-3">
            {/* Renders nothing unless this is a terminal with writes the server
                refused for good. Nothing else announces those: the till keeps
                working and the day looks normal, so silence is the failure mode
                this exists to break. It sits above the person rather than beside
                the sign-out, because in a `16rem` column a sentence and a button
                on one row is two truncated things. */}
            <RefusedWritesLink />

            {/* **The way back to the counter, on a terminal only (#259).** The
                till's sign-on screen opens this back office so an owner can
                register another outlet; this is how they return without
                restarting the app, and it signs them out as it goes so nobody
                is left signed in on a shared terminal. Nothing in a browser. */}
            <BackToTill
              variant="outline"
              size="touch"
              className="mb-3 w-full border-sidebar-border bg-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
            >
              Back to the till
            </BackToTill>

            {session !== null && (
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1 leading-tight">
                  <p className="truncate text-sm text-sidebar-foreground">
                    {session.user.displayName}
                  </p>
                  <p className="font-display text-[0.625rem] font-semibold tracking-[0.16em] text-sidebar-accent-foreground/75 uppercase">
                    {labelForRole(session.user.role)}
                  </p>
                </div>

                {/* `signOut` never rejects and never hangs — it clears this
                    browser whatever the request did — so there is nothing here
                    to catch and nothing to tell the user about. `void` because
                    the click handler is not async and a floating promise is a
                    swallowed failure everywhere it is not deliberate.

                    An icon button, because the word does not fit beside a name
                    in a `16rem` column. `aria-label` carries it instead, and the
                    icon is the one the whole app would use for this. Its hover
                    and focus are restated against the rail for the same reason
                    the disclosure's are. */}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Sign out"
                  className="border-sidebar-border text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring/30"
                  onClick={() => void signOut()}
                >
                  <IconLogout size={18} stroke={1.7} />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* THE FIELD. `min-w-0` is load-bearing beside a fixed-width column:
          without it a wide table inside a screen makes this track grow rather
          than letting the table scroll inside itself, and the whole page picks
          up a horizontal scrollbar. `min-h-0` for the same reason on the other
          axis — the panel states a height and the records inside `Screen` are
          what shrink to it. */}
      {/* The sheet. It carries the enamel and its own four corners, so its
          left edge notches the ground beside the rail rather than butting flat
          against it — the field reads as a sheet laid on the page rather than
          as the right-hand half of one shape.

          `overflow-hidden` moves here with the radius and is load-bearing: the
          panel's was what clipped `Screen`'s sticky `thead`, and without it the
          header paints over the rounded top edge.

          On the shell rather than on `Screen`, because not every route inside
          this frame uses `Screen` — `ChangeCredential` does not — and a route
          without one should still be a sheet rather than a hole in the ground.
          The Overview was the example until #216 made it a reporting screen;
          the argument did not depend on which route it was. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-card lg:overflow-hidden lg:rounded-[20px]">
        {/* A trading day before today still open anywhere this person can see
            (#335). Above every screen, and nothing at all when there is none. */}
        <OpenDaysWarning />
        {children}
      </div>
    </div>
  );
}
